import React, { Component } from "react";
import AsyncSelect from "react-select/async";
import { childrenOf, descendantsOf, levelsOf, errorText } from "../../../components/Dashboard/OrgChart/orgChartModel";

/**
 * HR chart editor — the panels on the right of the chart (approved design v4-people.html).
 * Each panel calls one action and shows a 422 next to itself; the editor redraws from the
 * draft every write returns.
 */

/** "Reporting to" choices: top level, then every person / open position in tree order. */
export const managerOptions = (items, exclude = []) => {
  const out = [{ value: "top", label: "Top level (no manager)" }];
  const walk = (parentId, depth) => {
    childrenOf(items, parentId).forEach((i) => {
      if (exclude.indexOf(i.id) === -1 && i.kind !== "team") {
        out.push({ value: String(i.id), label: " ".repeat(depth) + i.name + (i.title ? " · " + i.title : "") });
      }
      walk(i.id, depth + 1);
    });
  };
  walk(null, 0);
  return out;
};

const Panel = ({ icon, title, children }) => (
  <section className="oc-panel">
    <h5><i className={"fa " + icon} aria-hidden="true" /> {title}</h5>
    {children}
  </section>
);

/* ------------------------------ Add to chart ------------------------------ */

export class AddPanel extends Component {
  constructor(props) {
    super(props);
    this.state = { kind: "person", where: this.defaultWhere(props), people: [], label: "", title: "", headcount: "", saving: false, error: null };
  }

  defaultWhere(props) {
    const sel = props.selected;
    return sel && sel.parent_id !== undefined && sel.kind !== "team" && !sel.group ? String(sel.id) : "top";
  }

  componentDidUpdate(prev) {
    if ((prev.selected && prev.selected.id) !== (this.props.selected && this.props.selected.id)) {
      this.setState({ where: this.defaultWhere(this.props), error: null });
    }
  }

  submit = (e) => {
    e.preventDefault();
    const s = this.state;
    const data = { kind: s.kind };
    if (s.where.indexOf("group:") === 0) data.group_id = Number(s.where.slice(6));
    else if (s.where !== "top") data.parent_id = Number(s.where);
    if (s.kind === "person") {
      if (!s.people.length) return this.setState({ error: "Please choose at least one employee." });
      data.user_ids = s.people.map((p) => p.value);
    } else {
      if (!s.label.trim()) return this.setState({ error: s.kind === "team" ? "Please give the team a name." : "Please describe the open position." });
      if (s.kind === "open") {
        data.label = "Open position";
        data.title = s.label.trim();
      } else {
        data.label = s.label.trim();
        if (s.headcount) data.headcount = Number(s.headcount);
      }
    }
    this.setState({ saving: true, error: null });
    return this.props.onAdd(data)
      .then(() => this.setState({ saving: false, people: [], label: "", headcount: "" }))
      .catch((err) => this.setState({ saving: false, error: err && err.status === 422 ? errorText(err) : null }));
  };

  render() {
    const { items, groups, searchEmployees } = this.props;
    const s = this.state;
    const where = managerOptions(items).concat(s.kind === "person" ? groups.map((g) => ({ value: "group:" + g.id, label: "Side group: " + g.name })) : []);
    const peopleSearchTimeout = null;

    return (
      <Panel icon="fa-user-plus" title="Add to chart">
        <form onSubmit={this.submit} noValidate>
          <div className="oc-seg mb-2" role="radiogroup" aria-label="What to add">
            {[["person", "Employees"], ["open", "Open position"], ["team", "Team box"]].map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={s.kind === k} className={s.kind === k ? "on" : ""}
                onClick={() => this.setState({ kind: k, error: null, where: k !== "person" && s.where.indexOf("group:") === 0 ? "top" : s.where })}>{l}</button>
            ))}
          </div>
          <div className="form-group">
            <label className="oc-label" htmlFor="oc-add-where">{s.kind === "person" ? "Reporting to (or side group)" : "Reporting to"}</label>
            <select id="oc-add-where" className="form-control" value={s.where} onChange={(e) => this.setState({ where: e.target.value })}>
              {where.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {s.kind === "person" ? (
            <div className="form-group">
              <label className="oc-label" htmlFor="oc-add-people">Employees (one or more)</label>
              <AsyncSelect inputId="oc-add-people" isMulti cacheOptions value={s.people} onChange={(people) => this.setState({ people: people || [] })}
                loadOptions={(q) => {
                  if (!q || q.trim().length < 2) return Promise.resolve([]);
                  
                  clearTimeout(this.peopleSearchTimeout);
                  
                  return new Promise((resolve) => {
                    this.peopleSearchTimeout = setTimeout(() => {
                      resolve(searchEmployees(q.trim()));
                    }, 300); // 300ms debounce delay
                  });
                }}
                placeholder="Type at least 2 letters of a name"
                noOptionsMessage={({ inputValue }) => (inputValue.trim().length < 2 ? "Type at least 2 letters" : "No active employee found")} />
            </div>
          ) : (
            <div className="form-row">
              <div className={"form-group " + (s.kind === "team" ? "col-8" : "col-12")}>
                <label className="oc-label" htmlFor="oc-add-label">{s.kind === "team" ? "Team name" : "Position title"}</label>
                <input id="oc-add-label" className="form-control" maxLength={100} value={s.label} onChange={(e) => this.setState({ label: e.target.value })}
                  placeholder={s.kind === "team" ? "e.g. Morocco SDRs" : "e.g. Information Security Officer"} />
              </div>
              {s.kind === "team" && (
                <div className="form-group col-4">
                  <label className="oc-label" htmlFor="oc-add-count">People</label>
                  <input id="oc-add-count" type="number" min={1} max={9999} className="form-control" value={s.headcount} onChange={(e) => this.setState({ headcount: e.target.value })} />
                </div>
              )}
            </div>
          )}
          {s.error && <div className="invalid-feedback d-block mb-2" role="alert">{s.error}</div>}
          <button type="submit" className="btn btn-primary" disabled={s.saving}>
            <i className={"fa " + (s.saving ? "fa-spinner fa-spin" : "fa-plus")} aria-hidden="true" />Add to chart
          </button>
        </form>
      </Panel>
    );
  }
}

/* ------------------------------ Selected box ------------------------------ */

export class SelectedPanel extends Component {
  constructor(props) {
    super(props);
    this.state = this.fresh(props.item);
    this.fileRef = React.createRef();
  }

  fresh(item) {
    const details = {};
    (this.props.detailRows || []).forEach((r) => { details[r.id] = (item.details && item.details[r.id]) || ""; });
    return {
      title: item.title || "", details, label: item.label || "", headcount: item.headcount || "",
      parent: item.parent_id === null ? "top" : String(item.parent_id), saving: false, error: null, askRemove: false, children: "move_up",
    };
  }

  componentDidUpdate(prev) {
    if (prev.item.id !== this.props.item.id || prev.detailRows !== this.props.detailRows) this.setState(this.fresh(this.props.item));
  }

  fail = (err) => this.setState({ saving: false, error: err && err.status === 422 ? errorText(err) : null });

  save = (e) => {
    e.preventDefault();
    const { item } = this.props;
    const s = this.state;
    const data = {};
    if ((this.props.detailRows || []).length) {
      data.details = {};
      this.props.detailRows.forEach((r) => { data.details[r.id] = (s.details[r.id] || "").trim(); });
    }
    if (item.kind === "person") data.title = s.title.trim() || null;
    if (item.kind === "open") { data.title = s.title.trim() || null; }
    if (item.kind === "team") { data.label = s.label.trim(); data.headcount = s.headcount ? Number(s.headcount) : null; }
    if (!item.group) data.parent_id = s.parent;
    this.setState({ saving: true, error: null });
    return this.props.onUpdate(item.id, data).then(() => this.setState({ saving: false })).catch(this.fail);
  };

  move = (step) => {
    this.setState({ error: null });
    return this.props.onUpdate(this.props.item.id, { position: Math.max(1, this.props.item.position + step) }).catch(this.fail);
  };

  remove = () => {
    const kids = this.props.items.some((i) => i.parent_id === this.props.item.id);
    if (kids && !this.state.askRemove) return this.setState({ askRemove: true });
    if (!kids && !window.confirm("Remove " + this.props.item.name + " from this chart?")) return null;
    return this.props.onRemove(this.props.item.id, this.state.children).catch(this.fail);
  };

  upload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return null;
    if (["image/jpeg", "image/png", "image/webp"].indexOf(file.type) === -1) return this.setState({ error: "The photo must be a JPEG, PNG or WebP image." });
    if (file.size > 8 * 1024 * 1024) return this.setState({ error: "The photo may not be larger than 8 MB." });
    const fd = new FormData();
    fd.append("photo", file);
    return this.props.onUpload(this.props.item.id, fd).catch(this.fail);
  };

  render() {
    const { item, items } = this.props;
    const s = this.state;
    const kids = items.filter((i) => i.parent_id === item.id).length;
    const managers = managerOptions(items, [item.id].concat(descendantsOf(items, item.id)));
    const siblings = item.group ? [] : childrenOf(items, item.parent_id);
    const index = siblings.findIndex((i) => i.id === item.id);

    return (
      <Panel icon="fa-pencil" title={item.name}>
        {item.left && <div className="alert alert-danger py-1 px-2 mb-2">Left the company. Staff no longer see this person. Remove them or replace them.</div>}
        <form onSubmit={this.save} noValidate>
          {item.kind === "team" ? (
            <div className="form-row">
              <div className="form-group col-8"><label className="oc-label" htmlFor="oc-sel-label">Team name</label>
                <input id="oc-sel-label" className="form-control" maxLength={100} value={s.label} onChange={(e) => this.setState({ label: e.target.value })} /></div>
              <div className="form-group col-4"><label className="oc-label" htmlFor="oc-sel-count">People</label>
                <input id="oc-sel-count" type="number" min={1} className="form-control" value={s.headcount} onChange={(e) => this.setState({ headcount: e.target.value })} /></div>
            </div>
          ) : (
            <div className="form-group"><label className="oc-label" htmlFor="oc-sel-title">Title shown on the chart</label>
              <input id="oc-sel-title" className="form-control" maxLength={100} value={s.title} onChange={(e) => this.setState({ title: e.target.value })} /></div>
          )}
          {(this.props.detailRows || []).map((r) => (
            <div className="form-group" key={r.id}>
              <label className="oc-label" htmlFor={"oc-sel-d-" + r.id}>{r.label}</label>
              <input id={"oc-sel-d-" + r.id} className="form-control" maxLength={120} value={s.details[r.id] || ""}
                onChange={(e) => { const v = e.target.value; this.setState((st) => ({ details: { ...st.details, [r.id]: v } })); }} />
            </div>
          ))}
          {!item.group && (
            <div className="form-group"><label className="oc-label" htmlFor="oc-sel-parent">Reports to</label>
              <select id="oc-sel-parent" className="form-control" value={s.parent} onChange={(e) => this.setState({ parent: e.target.value })}>
                {managers.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select></div>
          )}
          {s.error && <div className="invalid-feedback d-block mb-2" role="alert">{s.error}</div>}
          <div className="d-flex flex-wrap" style={{ gap: 6 }}>
            {!item.group && <button type="button" className="btn btn-secondary btn-sm" onClick={() => this.move(-1)} disabled={index <= 0}><i className="fa fa-arrow-left" aria-hidden="true" />Move left</button>}
            {!item.group && <button type="button" className="btn btn-secondary btn-sm" onClick={() => this.move(1)} disabled={index === -1 || index >= siblings.length - 1}><i className="fa fa-arrow-right" aria-hidden="true" />Move right</button>}
            <button type="submit" className="btn btn-primary btn-sm" disabled={s.saving}><i className="fa fa-save" aria-hidden="true" />Save</button>
            <button type="button" className="btn btn-danger btn-sm" onClick={this.remove}><i className="fa fa-trash" aria-hidden="true" />Remove</button>
          </div>
        </form>
        {s.askRemove && (
          <div className="oc-ask mt-2" role="group" aria-label="What happens to their people">
            <p className="mb-1"><b>{kids === 1 ? "1 person reports" : kids + " people report"} to {item.name}.</b> What should happen to them?</p>
            <div className="custom-control custom-radio"><input type="radio" id="oc-rm-up" className="custom-control-input" checked={s.children === "move_up"} onChange={() => this.setState({ children: "move_up" })} />
              <label className="custom-control-label" htmlFor="oc-rm-up">Move them up to {item.parent_id ? "the next manager" : "the top level"}</label></div>
            <div className="custom-control custom-radio"><input type="radio" id="oc-rm-all" className="custom-control-input" checked={s.children === "remove"} onChange={() => this.setState({ children: "remove" })} />
              <label className="custom-control-label" htmlFor="oc-rm-all">Remove them too</label></div>
            <div className="mt-2"><button type="button" className="btn btn-danger btn-sm" onClick={this.remove}>Remove</button>{" "}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => this.setState({ askRemove: false })}>Cancel</button></div>
          </div>
        )}
        {item.kind === "person" && (
          <div className="oc-photo-row mt-3">
            <span className="oc-label mb-0">Photo</span>
            <button type="button" className="btn btn-light border btn-sm" onClick={() => this.fileRef.current && this.fileRef.current.click()}><i className="fa fa-upload" aria-hidden="true" />Upload</button>
            <input ref={this.fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="d-none" aria-label="Upload photo" data-testid="oc-photo-input" onChange={this.upload} />
          </div>
        )}
      </Panel>
    );
  }
}

/* ------------------------------ Detail rows ------------------------------- */

/**
 * The chart's detail rows (e.g. Domains, Sponsor): shown under every person — as aligned bands on
 * a flat chart, as labelled lines in tree cards. Up to 4. Every change saves the whole list;
 * rows keep their id, so renaming or moving a row keeps each person's value.
 */
export class DetailRowsPanel extends Component {
  state = { name: "", editing: null, editName: "", error: null };

  fail = (err) => this.setState({ error: err && err.status === 422 ? errorText(err) : null });

  save = (rows) => {
    this.setState({ error: null });
    return this.props.onSave(rows.map((r) => ({ id: r.id, label: r.label }))).catch((e) => { this.fail(e); throw e; });
  };

  add = (e) => {
    e.preventDefault();
    const name = this.state.name.trim();
    if (!name) return this.setState({ error: "Please give the detail row a name." });
    if (this.props.rows.length >= 4) return this.setState({ error: "A chart can have at most 4 detail rows." });
    return this.save(this.props.rows.concat([{ id: null, label: name }])).then(() => this.setState({ name: "" })).catch(() => null);
  };

  move = (index, step) => {
    const rows = this.props.rows.slice();
    const [r] = rows.splice(index, 1);
    rows.splice(index + step, 0, r);
    return this.save(rows).catch(() => null);
  };

  rename = (e, id) => {
    e.preventDefault();
    const name = this.state.editName.trim();
    if (!name) return this.setState({ error: "Every detail row needs a name." });
    return this.save(this.props.rows.map((r) => (r.id === id ? { ...r, label: name } : r))).then(() => this.setState({ editing: null })).catch(() => null);
  };

  remove = (row) => {
    if (!window.confirm('Remove the detail row "' + row.label + '"? Its value is removed from every person in this chart.')) return null;
    return this.save(this.props.rows.filter((r) => r.id !== row.id)).catch(() => null);
  };

  render() {
    const { rows } = this.props;
    const s = this.state;
    return (
      <Panel icon="fa-list-ul" title="Detail rows">
        {rows.map((r, i) => (
          <div key={r.id} className="oc-row-bar">
            {s.editing === r.id ? (
              <form className="d-flex w-100" style={{ gap: 4 }} onSubmit={(e) => this.rename(e, r.id)}>
                <input className="form-control form-control-sm" aria-label="Detail row name" maxLength={40} value={s.editName} onChange={(e) => this.setState({ editName: e.target.value })} />
                <button type="submit" className="btn btn-primary btn-sm">Save</button>
              </form>
            ) : (
              <>
                <b>{r.label}</b>
                <span className="text-nowrap">
                  <button type="button" className="oc-row-act" onClick={() => this.move(i, -1)} disabled={i === 0} aria-label={"Move " + r.label + " up"}><i className="fa fa-arrow-up" aria-hidden="true" /></button>
                  <button type="button" className="oc-row-act" onClick={() => this.move(i, 1)} disabled={i === rows.length - 1} aria-label={"Move " + r.label + " down"}><i className="fa fa-arrow-down" aria-hidden="true" /></button>
                  <button type="button" className="oc-row-act" onClick={() => this.setState({ editing: r.id, editName: r.label })} aria-label={"Rename " + r.label}><i className="fa fa-pencil" aria-hidden="true" /></button>
                  <button type="button" className="oc-row-act red" onClick={() => this.remove(r)} aria-label={"Remove " + r.label}><i className="fa fa-trash" aria-hidden="true" /></button>
                </span>
              </>
            )}
          </div>
        ))}
        {rows.length < 4 && (
          <form className="d-flex mt-2" style={{ gap: 6 }} onSubmit={this.add} noValidate>
            <input className="form-control" aria-label="New detail row name" maxLength={40} placeholder="e.g. Domains, Sponsor" value={s.name} onChange={(e) => this.setState({ name: e.target.value })} />
            <button type="submit" className="btn btn-secondary text-nowrap"><i className="fa fa-plus" aria-hidden="true" />Add row</button>
          </form>
        )}
        {s.error && <div className="invalid-feedback d-block" role="alert">{s.error}</div>}
        <p className="text-muted mt-2 mb-0" style={{ fontSize: 11 }}>Shown under every person in this chart. Fill the values in each person's panel; leave blank where it doesn't apply.</p>
      </Panel>
    );
  }
}

/* ------------------------------ Side groups ------------------------------ */

export class GroupsPanel extends Component {
  state = { name: "", error: null, editing: null, editName: "" };

  fail = (err) => this.setState({ error: err && err.status === 422 ? errorText(err) : null });

  add = (e) => {
    e.preventDefault();
    if (!this.state.name.trim()) return this.setState({ error: "Please give the side group a name." });
    return this.props.onAdd(this.state.name.trim()).then(() => this.setState({ name: "", error: null })).catch(this.fail);
  };

  render() {
    const { groups, onRename, onDelete } = this.props;
    const s = this.state;
    return (
      <Panel icon="fa-columns" title="Side groups">
        {groups.map((g) => (
          <div key={g.id} className="oc-grp-bar">
            {s.editing === g.id ? (
              <form className="d-flex w-100" style={{ gap: 4 }} onSubmit={(e) => { e.preventDefault(); onRename(g.id, s.editName.trim()).then(() => this.setState({ editing: null })).catch(this.fail); }}>
                <input className="form-control form-control-sm" aria-label="Side group name" maxLength={80} value={s.editName} onChange={(e) => this.setState({ editName: e.target.value })} />
                <button type="submit" className="btn btn-primary btn-sm">Save</button>
              </form>
            ) : (
              <>
                <span>{g.name} · {g.items.length}</span>
                <span>
                  <button type="button" className="oc-bar-act" onClick={() => this.setState({ editing: g.id, editName: g.name })} aria-label={"Rename " + g.name}><i className="fa fa-pencil" aria-hidden="true" /></button>
                  <button type="button" className="oc-bar-act" onClick={() => window.confirm('Remove the side group "' + g.name + '" and the people in it?') && onDelete(g.id).catch(this.fail)} aria-label={"Remove " + g.name}><i className="fa fa-trash" aria-hidden="true" /></button>
                </span>
              </>
            )}
          </div>
        ))}
        <form className="d-flex mt-2" style={{ gap: 6 }} onSubmit={this.add} noValidate>
          <input className="form-control" aria-label="New side group name" maxLength={80} placeholder="e.g. EXECO Sales Support" value={s.name} onChange={(e) => this.setState({ name: e.target.value })} />
          <button type="submit" className="btn btn-secondary text-nowrap"><i className="fa fa-plus" aria-hidden="true" />Add</button>
        </form>
        {s.error && <div className="invalid-feedback d-block" role="alert">{s.error}</div>}
        <p className="text-muted mt-2 mb-0" style={{ fontSize: 11 }}>People shown beside the chart without reporting lines. Add people to a group with "Add to chart".</p>
      </Panel>
    );
  }
}

/* ------------------------------ Remove a level ----------------------------- */

export const LevelPanel = ({ items, onRemoveLevel }) => {
  const levels = levelsOf(items);
  const max = Object.values(levels).reduce((m, l) => Math.max(m, l), 0);
  if (max < 2) return null;
  const options = [];
  for (let l = max; l >= 2; l--) {
    const n = Object.values(levels).filter((x) => x >= l).length;
    options.push({ value: l, label: "Level " + l + (l < max ? " and below" : "") + " (" + n + (n === 1 ? " box)" : " boxes)") });
  }
  let select;
  return (
    <Panel icon="fa-scissors" title="Remove a level">
      <div className="d-flex" style={{ gap: 6 }}>
        <select className="form-control" aria-label="Level to remove" ref={(el) => { select = el; }} defaultValue={options[0].value}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button type="button" className="btn btn-danger"
          onClick={() => { const l = Number(select.value); if (window.confirm("Remove level " + l + " and everything below it from this chart?")) onRemoveLevel(l); }}>Remove</button>
      </div>
      <p className="text-muted mt-2 mb-0" style={{ fontSize: 11 }}>Cuts that level and everyone below it in one step.</p>
    </Panel>
  );
};
